import {
  EMAIL_REGEX,
  NUMBER_REGEX,
  APLHANUMERIC_WITH_ONE_SPACE_REGEX,
  SORT_CODE_REGEX,
  BRANCH_CODE_REGEX,
  ACCOUNT_NUMBER_REGEX,
  MAX_MAKE_AN_OFFER_AMOUNT,
} from '../consts';
import { passwordRequirementsCheck } from '../components/form/password_hint';
import { amountToInteger } from './form_utils';

export const validatePassword = (values, { fieldError = 'default' } = {}) =>
  !values['password'] ? { password: fieldError } : {};

export const validateEmail = (
  values,
  fieldName = 'email',
  { fieldError = 'default' } = {}
) => {
  if (!values[fieldName]) return { [fieldName]: fieldError };
  var trimmedValue = values[fieldName].trim();
  if (trimmedValue && !EMAIL_REGEX.test(trimmedValue)) {
    return { [fieldName]: fieldError };
  }
  return {};
};

export const validateValueNotTaken = (
  values,
  fieldName = '',
  { valuesTaken = [], fieldError = 'taken' } = {}
) => {
  const fieldValue = values[fieldName];
  if (fieldValue) {
    const valuesTakenMatched = valuesTaken.filter(
      (value) => value.toLowerCase() === fieldValue.toLowerCase()
    );
    return valuesTakenMatched.length ? { [fieldName]: fieldError } : {};
  }
  return {};
};

export const validateMobileNumber = (
  values,
  { fieldError = 'default' } = {}
) => {
  return !values['phone'] || !NUMBER_REGEX.test(values['phone'])
    ? { phone: fieldError }
    : {};
};

export const validateBranchCode = (values, { fieldError = 'default' } = {}) => {
  return !values['bic'] || !BRANCH_CODE_REGEX.test(values['bic'])
    ? { bic: fieldError }
    : {};
};

export const validatePasswordRequiredIfNewNotEmpty = (
  values,
  { fieldError = 'default' } = {}
) => {
  return (!values['password'] || !values['password'].trim()) &&
    values['new-password']
    ? { password: fieldError }
    : {};
};

export const validatePasswordComplexity = (
  values,
  fieldName = 'new-password',
  { fieldError = 'default' } = {}
) => {
  return values[fieldName] &&
    Object.entries(passwordRequirementsCheck).reduce((state, [_, checkFn]) => {
      const fullfilled = checkFn(values[fieldName]);
      return state || !fullfilled;
    }, false)
    ? { [fieldName]: fieldError }
    : {};
};

export const validateNotEmpty = (
  values,
  fields,
  { fieldError = 'default' } = {}
) => {
  return fields.reduce((acc, field) => {
    const fieldState =
      !values[field] || !values[field].trim() ? { [field]: fieldError } : {};
    return { ...acc, ...fieldState };
  }, {});
};

export const validateIsTrue = (
  values,
  fields,
  { fieldError = 'default' } = {}
) => {
  return fields.reduce((acc, field) => {
    const fieldState =
      !values[field] || JSON.parse(values[field]) === false
        ? { [field]: fieldError }
        : {};
    return { ...acc, ...fieldState };
  }, {});
};

export const validateSortCode = (
  values,
  fields,
  { fieldError = 'default' } = {}
) => {
  return fields.reduce((acc, field) => {
    const fieldState =
      !values[field] || !SORT_CODE_REGEX.test(values[fields])
        ? { [field]: fieldError }
        : {};
    return { ...acc, ...fieldState };
  }, {});
};

export const validateAccountNumber = (
  values,
  fields,
  { fieldError = 'default' } = {}
) => {
  return fields.reduce((acc, field) => {
    const fieldState =
      !values[field] || !ACCOUNT_NUMBER_REGEX.test(values[fields])
        ? { [field]: fieldError }
        : {};
    return { ...acc, ...fieldState };
  }, {});
};

export const validateAlphanumeric = (
  values,
  fields,
  { fieldError = 'default' } = {}
) => {
  return fields.reduce((acc, field) => {
    const fieldState =
      !values[field] || !APLHANUMERIC_WITH_ONE_SPACE_REGEX.test(values[field])
        ? { [field]: fieldError }
        : {};
    return { ...acc, ...fieldState };
  }, {});
};

export const validateMakeAnOffer = (
  values,
  fieldName = 'offer_amount',
  { fieldError = 'out-of-range' } = {}
) => {
  return amountToInteger(values[fieldName]) > MAX_MAKE_AN_OFFER_AMOUNT
    ? { [fieldName]: fieldError }
    : {};
};

export const validateMaxLength = (
  values,
  fieldName = '',
  { fieldError = 'max-length' } = {},
  maxLength
) => {
  return values[fieldName]?.length > maxLength
    ? { [fieldName]: fieldError }
    : {};
};

const whatIsYourOccupationStatusField = 'occupation_status';
const whatIsYourOccupationField = 'occupation_status_option';
const whatIsYourOccupationOtherField = 'occupation_other';
const statusFieldsThatNeedAdditionalCheck = [
  'self-employed',
  'employed',
  'retired',
];

export const validateWhatIsYourOccupation = (
  values,
  { fieldError = 'default' } = {}
) => {
  // check if what is your occupation section is present in form
  if (whatIsYourOccupationStatusField in values) {
    const statusFieldValue = values[whatIsYourOccupationStatusField];
    // check if selected status require selection from What Is Your Occupation dropdown
    if (statusFieldsThatNeedAdditionalCheck.includes(statusFieldValue)) {
      const occupationFieldValue = values[whatIsYourOccupationField];
      // check if selected occupation is 'other' which requires additional input
      if (occupationFieldValue === 'other') {
        const otherOccupationFieldValue =
          values[whatIsYourOccupationOtherField];
        // check if other occupation is left empty
        if (otherOccupationFieldValue === '') {
          return {
            [whatIsYourOccupationOtherField]: fieldError,
          };
        }
      } else {
        // return error if occupation field is left unselected
        if (occupationFieldValue === '') {
          return {
            [whatIsYourOccupationField]: fieldError,
          };
        }
      }
    } else {
      // return error if occupation status field is left unselected
      if (statusFieldValue === '') {
        return {
          [whatIsYourOccupationStatusField]: fieldError,
        };
      }
    }
  }

  // if none of above is missing then return no errors
  return {};
};
